/**
 * Research centre catalogue. Slug used at /research/centers/:slug.
 *
 * Source: BIET_RESOURCE/Institute-website-research data.docx — the collection
 * form the Dean (Research) office circulated, returned by eleven centre
 * coordinators. Every value below is transcribed from that return.
 *
 * Nothing here is inferred or filled in. Where a coordinator left a required
 * field blank, returned template text, or answered "Nil", the field is null or
 * an empty array and the page omits that block rather than inventing a value.
 * The outstanding items are listed in RESEARCH_DATA_GAPS at the foot of this
 * file — that is the chase-list for the Dean's office, not a TODO for the web
 * team.
 *
 * Two counts the form asks for are deliberately NOT derived from the rows
 * below: `projects` is the coordinator's own count of funded projects, which
 * is larger than the number of project rows they listed for four centres, and
 * the institute totals in INSTITUTE_RESEARCH are the Dean's office figures.
 * Both are reproduced as submitted; see RESEARCH_DATA_GAPS.
 */

export interface ResearchProject {
  title: string;
  /** Empty when the return did not name one — the table renders "—". */
  pi: string;
  funder: string;
  /** Sanctioned amount as submitted, normalised to lakhs. */
  amount?: string;
  status: "ongoing" | "completed";
}

/** Patent record. Only EE-RD returned a structured patent table. */
export interface Patent {
  faculty: string;
  title: string;
  office: string;
  number: string;
  published?: string;
  granted?: string;
}

export interface ResearchCenter {
  slug: string;
  name: string;
  short: string;
  /**
   * Year of VTU recognition, as submitted. A string, not a number: three
   * centres returned a range or a full date ("2008-09", "2001 & 2008",
   * "04 October 2024") and rounding those to a year would misstate them.
   */
  estd: string;
  desc: string;
  /**
   * Department codes this centre is operated by — these are `code` values from
   * departments.ts, so getCentersByDept() resolves. Centres are often
   * interdisciplinary, so this is an array. Coordinators answered this field in
   * free text; where their answer did not map cleanly, `deptRaw` preserves what
   * they actually wrote.
   */
  departmentCodes: string[];
  /** The coordinator's verbatim answer, kept only where it differs from the mapping. */
  deptRaw?: string;
  /** null where the return said "Nil"/"NIL"/"NA" — rendered as "—", never 0. */
  scholars: number | null;
  projects: number | null;
  pubs: number | null;
  /** Qualifier the coordinator attached to the publication count, if any. */
  pubsNote?: string;
  /** Long-form intro paragraphs. Empty when the centre returned none. */
  intro: string[];
  /** Tag chips representing the centre's focus. */
  focus: string[];
  /** staff.ts slugs — the real employee record, resolved by name. */
  facultySlugs: string[];
  ongoing: ResearchProject[];
  patents: Patent[];
  partners: string[];
  highlights: string[];
  contact: { coordinator: string; email: string; room: string };
}

export const CENTERS: ResearchCenter[] = [
  {
    slug: "chemical-engineering",
    name: "Department of Chemical Engineering",
    short: "CH",
    estd: "2008–09",
    desc: "Pioneering sustainable process solutions and advanced materials through cutting-edge chemical engineering research.",
    departmentCodes: ["CH"],
    scholars: 4,
    projects: 13,
    pubs: 6,
    intro: [
      "The Chemical Engineering Research Centre at the Bapuji Institute of Engineering and Technology (BIET) drives industrial innovation and academic excellence in applied chemical sciences. Recognized as a comprehensive R&D node under Visvesvaraya Technological University (VTU), the Centre translates fundamental thermodynamics, fluid dynamics, and molecular science into scalable, sustainable engineering solutions. By supporting doctoral scholars and executing actively sponsored research projects, the facility serves as a vital bridge between rigorous theoretical study and real-world industrial application.",
      "Research operations are anchored in specialized, high-impact thrust areas: sustainable bio-energy, advanced materials, and environmental remediation. Multidisciplinary teams actively investigate the thermal characterization and combustion efficiency of biodiesel blends, the synthesis of dual-functional nanocomposites for photocatalytic degradation, and the development of conducting polymers for electrochemical sensing. Driven by continuous funding from external agencies, the Centre fosters a collaborative ecosystem dedicated to publishing peer-reviewed insights, securing patents, and pioneering new methodologies in process optimization.",
      "The Centre is powered by a robust network of specialized laboratories and computational infrastructure. Investigators have direct access to advanced analytical instrumentation, including a Fourier Transform Infrared Spectrophotometer (FTIR) and a Super Critical Fluid Extractor (SFE 100) procured through VGST grants. Physical experimentation is complemented by high-performance simulation labs equipped with UNISIM software for rigorous process scale-up and design. Additionally, dedicated pollution control and project laboratories provide the essential tools for water quality monitoring, materials synthesis, and chemical reaction engineering.",
    ],
    focus: [
      "Biofuels & Renewable Energy",
      "Advanced Materials & Nanotechnology",
      "Process Simulation & Computational Fluid Dynamics (CFD)",
      "Environmental Remediation & Pollution Control",
    ],
    // G P Desai is named in the return but has no record in staff.ts — see gaps.
    facultySlugs: [
      "c-m-kalleshappa",
      "praveen-kumar-d-g",
      "shashikala-k-j",
      "amarnath-p-c",
      "srinidhi-r-kulkarni",
    ],
    ongoing: [
      { title: "Development of Thermoelectric Coolers for Remote Power Generation Using Bismuth Telluride (Bi₂Te₃) Based Materials", pi: "Dr. Praveen Kumar D G", funder: "NAIN", amount: "₹2.00 L", status: "completed" },
      { title: "Design and Development of a Hydrothermal Reactor for recovery of high purity Silica from Black Liquor", pi: "Srinidhi R Kulkarni", funder: "NAIN", amount: "₹4.72 L", status: "ongoing" },
      { title: "Designing a Cost-Effective Method for the Synthesis of Polymer Nanocomposites for Supercapacitor Applications", pi: "Shashikala K J", funder: "KSCST", amount: "₹0.06 L", status: "completed" },
    ],
    patents: [],
    // The return listed VGST / VTU / NAIN 2.0 under "Highlights" and left
    // "Funders & partners" as the form's own examples. They are funders, so
    // they are carried here; the Dean's office is confirming.
    partners: [
      "Vision Group on Science and Technology (VGST)",
      "Visvesvaraya Technological University (VTU)",
      "New Age Innovation Network 2.0",
    ],
    highlights: [],
    contact: {
      coordinator: "Dr. C M Kalleshappa — Professor & Head, Chemical Engineering",
      email: "cmkalleshappa@bietdvg.edu",
      room: "",
    },
  },
  {
    slug: "chemistry",
    name: "Chemistry Research Centre",
    short: "CHE",
    estd: "2002",
    desc: "Synthetic organic chemistry, medicinal chemistry, nanocellulose, corrosion studies and metal complexes.",
    departmentCodes: ["CHE"],
    scholars: 9,
    projects: null,
    pubs: 20,
    intro: [
      "The Department was recognized as the first Research Centre under Visvesvaraya Technological University (VTU), Belagavi, in the year 2002. The Department offers Ph.D. programmes in the areas of Nanotechnology, Corrosion Chemistry, Medicinal Chemistry, and Dye and Coordination Chemistry.",
      "A total of 18 Ph.D. scholars have been awarded doctoral degrees in association with ESTSC, while 07 scholars have been awarded Ph.D. degrees under chemistry and 9 scholars are currently pursuing their research through the Chemistry Research Centre.",
      "The faculty members of the Department are actively engaged in research and have published numerous high-quality research papers in reputed national and international journals. The Department continues to promote innovative and interdisciplinary research in emerging areas of chemistry and nanotechnology.",
    ],
    focus: [
      "Synthetic Organic Chemistry",
      "Medicinal Chemistry",
      "Nanocellulose",
      "Nanofluids",
      "Corrosion Studies",
      "Metal Complexes",
    ],
    facultySlugs: [
      "prasannakumar-j-k",
      "mallikarjuna-h-r",
      "b-m-prasanna",
      "manjunath-k-s",
      "pavithra-k-s",
    ],
    ongoing: [],
    patents: [],
    partners: [],
    highlights: [
      "Patent published — Self-Powered IoT Biomedical Device Using BiFeO₃ Energy Harvesting and Machine Learning-Based Data Interpretation, 2025",
      "Patent published — Novel Azo Dye as corrosion inhibitor for mild steel, 2021",
    ],
    contact: {
      coordinator: "Dr. Prasannakumar J K — Associate Professor & Head, Chemistry",
      email: "hodchemistrybiet@gmail.com",
      room: "Textile Block · Room T-110",
    },
  },
  {
    slug: "environmental-civil",
    name: "Centre for Environmental and Civil Engineering Research",
    short: "ENV & CV",
    estd: "2001 & 2008",
    desc: "Structural engineering, geotechnical engineering, transportation, environmental engineering and water resources.",
    departmentCodes: ["CV", "ENV"],
    deptRaw: "PEV, PCV",
    scholars: 20,
    projects: null,
    pubs: 5,
    intro: [
      "The Centre for Environmental and Civil Engineering Research (ENV & CV) at B.I.E.T. Davangere provides an interdisciplinary platform for research in sustainable infrastructure, environmental protection, and advanced structural solutions. Established through the integration of expertise in 2001 and 2008, the centre brings together experimental and practical engineering methods to solve complex challenges in civil infrastructure and environmental management. Its major research areas span Structural Engineering, Geotechnical Engineering, Transportation Engineering, Environmental Engineering, and Water Resources Engineering. The centre fosters a vibrant academic ecosystem, currently supporting 20 active research scholars, and drives regional innovation to address future scientific, societal, and technological infrastructure needs.",
    ],
    focus: ["Structural Engineering", "Geotechnical Engineering", "Environmental Engineering"],
    facultySlugs: [
      "h-b-aravind",
      "anilakumar-c-p",
      "suresh-s",
      "chidananda-g",
      "suresh-b",
      "basavanagowda-s-n",
      "raghu-m-e",
    ],
    ongoing: [],
    patents: [],
    partners: [],
    highlights: [],
    contact: {
      coordinator: "Dr. Chidananda G — Professor & Head, Civil Engineering",
      email: "chidanandag@bietdvg.edu",
      room: "Civil Block · Room CV-205",
    },
  },
  {
    slug: "computer-science",
    name: "Computer Science and Engineering Research Centre",
    short: "CS&E",
    estd: "2000",
    desc: "AI/ML, data science, big data analytics, cloud computing, cybersecurity, IoT, image processing and intelligent systems.",
    departmentCodes: ["CS", "CG", "CD"],
    deptRaw: "Computer Science and Engineering (CS&E, CS & Design and CS (Data Science))",
    scholars: 34,
    projects: 0,
    pubs: 74,
    intro: [
      "The Research Center of the Department of Computer Science and Engineering at Bapuji Institute of Engineering and Technology is recognized by Visvesvaraya Technological University as a Research and Development Center. With over two decades of research excellence, it serves as a hub for innovation, advanced research, and knowledge creation. The center promotes collaborative research among faculty, scholars, and students to address real-world challenges.",
      "Research areas include Artificial Intelligence and Machine Learning, Data Science, Big Data Analytics, Cloud Computing, Cyber Security, Image Processing, IoT, and Intelligent Systems. Equipped with modern infrastructure and access to research resources, it provides a conducive environment for innovation, and experienced research supervisors guide doctoral scholars toward impactful research outcomes.",
      "The center encourages interdisciplinary research, industry collaborations, funded projects, patents, and quality publications. It also actively involves students in research-oriented activities and emerging technologies. Guided by the vision “Innovating Today for a Smarter Tomorrow,” it transforms ideas into meaningful technological solutions that benefit society.",
    ],
    focus: [
      "Artificial Intelligence & Agentic AI",
      "Generative AI & Large Language Models",
      "Machine Learning",
      "Cybersecurity & Digital Forensics",
      "Internet of Things (IoT) & AIoT",
      "Cloud & Edge Computing",
      "Blockchain & Web3",
      "Intelligent Robotics & Automation",
      "Deep Learning",
    ],
    facultySlugs: [
      "nirmala-c-r",
      "chetana-prakash",
      "pradeep-n",
      "sreenivasa-b-r",
      "gururaj-t",
      "jyothi-g-c",
      "m-s-abdul-razak",
      "santhosh-k-c",
      "naseer-r",
      "naresh-patel-k-m",
      "naveen-h-m",
      "gangadharappa-s",
      "anusha-r",
      "usha-g-r",
      "prashantha-g-r",
    ],
    ongoing: [],
    patents: [],
    partners: [],
    highlights: ["Three patents published and one design patent granted"],
    contact: {
      coordinator: "Dr. Chetana Prakash — Professor, CS&E",
      email: "chetana.p.m@gmail.com",
      room: "Room A-333B",
    },
  },
  {
    slug: "electronics-communication",
    name: "Electronics & Communication Engineering Research Centre",
    short: "E & CE",
    estd: "2020",
    desc: "Communication and networks, VLSI and embedded systems, biomedical signal processing, microwave engineering and applied machine learning.",
    departmentCodes: ["EC"],
    scholars: 5,
    projects: 20,
    pubs: 72,
    pubsNote: "including 6 Scopus-indexed",
    intro: [
      "The Research Center was established in the year 2020. It offers Ph.D. programmes in the areas such as Digital Communication & Computer Networks, Network Security, Wireless Communication Networking, Image Processing, Biomedical Instrumentation, Biomedical Signal Processing, VLSI Design & Embedded Systems, Microwave Engineering, Coding Theory, Satellite Image Processing, Machine Learning, Deep Learning and related emerging technologies.",
      "Currently 5 scholars are pursuing their research on a part-time basis in VLSI Design, Machine Learning, Deep Learning and Microwave Engineering. The department also provides full-time research.",
      "The Centre provides shared laboratory infrastructure, computing facilities, Electronic Design Automation tools, FPGA and Embedded development platforms, and Communication equipment to enable researchers to carry out design, simulation, prototyping, testing, and validation. It also encourages collaborative research, technical publications, interdisciplinary projects, and the development of innovative solutions with academic, industrial, and societal relevance.",
    ],
    focus: [
      "Digital Communication & Computer Networks",
      "Network Security",
      "Wireless Communication Networking",
      "Image Processing",
      "Biomedical Instrumentation",
      "Biomedical Signal Processing",
      "VLSI Design & Embedded Systems",
      "Microwave Engineering",
      "Coding Theory",
      "Satellite Image Processing",
      "Machine Learning",
      "Deep Learning",
    ],
    facultySlugs: [
      "g-s-sunitha",
      "leela-g-h",
      "s-o-nirmala",
      "avinash-k-g",
      "k-m-prakash",
      "g-h-kiran-kumar",
      "banumathi-k-l",
      "shubha-v-patel",
      "pavan-kumar-d",
      "tejashwini-y",
    ],
    ongoing: [
      { title: "Establishment of Agritech Lab for Research and Development of Super Food Indian Millets in Davanagere Region", pi: "Dr. Leela G H", funder: "VGST", amount: "₹30.00 L", status: "ongoing" },
      { title: "Design and Development of Soil Testing based crop monitoring", pi: "Dr. Tejashwini Y", funder: "NAIN", amount: "₹2.00 L", status: "completed" },
      { title: "Generative AI — Democratization of AI, Responsible AI", pi: "Dr. Nirmala S O", funder: "ATAL FDP", amount: "₹0.95 L", status: "completed" },
      { title: "Pro Indo Keys", pi: "Mrs. Bhagya Shanthakumar", funder: "NAIN", amount: "₹1.70 L", status: "completed" },
    ],
    patents: [],
    partners: [
      "Silicon Microsystem Pvt. Ltd., Bangalore",
      "NAIN, Department of S&T, Govt. of Karnataka",
      "VGST, Govt. of Karnataka",
      "ATAL, AICTE New Delhi",
    ],
    highlights: [
      "Device for Monitoring Intra-Circuit Energy Consumption — patent applied, application no. 454175-001, under examination",
      "VGST grant of ₹30 lakh for the Agritech Lab for Super Food Indian Millets in the Davanagere region",
    ],
    contact: {
      coordinator: "Dr. G S Sunitha — Professor & Head, E&CE",
      email: "drsunithags_ece@bietdvg.edu",
      room: "Admin Block · 3rd Floor · Research Lab A-351",
    },
  },
  {
    slug: "electrical-electronics",
    name: "Electrical and Electronics Engineering Research Centre",
    short: "EE-RD",
    estd: "2008",
    desc: "Artificial neural networks, fuzzy logic, distributed generation, smart grid, renewable energy and advanced control for power systems.",
    departmentCodes: ["EE"],
    scholars: 3,
    projects: 5,
    pubs: 36,
    intro: [
      "The Electrical and Electronics Engineering Research Centre was established in 2008 under Visvesvaraya Technological University, Belagavi. The centre has three research supervisors who conduct research in the following focus areas: power systems operation, analysis, and control; artificial intelligence applications to power systems; renewable energy sources; and smart-grid operation with advanced control systems.",
      "The centre has awarded Ph.D. degrees to five research scholars in different areas — distributed generation, electrical power quality, interline power flow model, automatic generation control, and control strategies for microgrid — under VTU, Belagavi.",
    ],
    focus: [
      "Power Systems",
      "AI Techniques",
      "Smart Grid",
      "Renewable Energy Sources",
      "Advanced Control Systems",
    ],
    facultySlugs: ["anjaneya-l-h", "s-r-basavarajappa", "manjunatha-h-m"],
    ongoing: [
      { title: "Smart Solar Network: IoT Application for Grid-Connected Rooftop SPV Powered Prosumer", pi: "Dr. Manjunatha H M", funder: "New Age Incubation Network (NAIN)", amount: "₹4.84 L", status: "ongoing" },
      { title: "IoT Based Automated Ration System using Arduino", pi: "T S Karibasavaraju · Dr. M S Nagaraj", funder: "New Age Incubation Network (NAIN)", amount: "₹2.46 L", status: "completed" },
      { title: "Modernisation of Power Systems Simulation Laboratory", pi: "Dr. M S Nagaraj", funder: "AICTE, New Delhi (MODROBS)", amount: "₹18.30 L", status: "completed" },
      { title: "Study of Flashover Voltage of Artificially Polluted Porcelain Insulators Energized with High Voltage AC / DC", pi: "Pradip Kumar Dixit", funder: "AICTE, New Delhi (RPS)", amount: "₹7.00 L", status: "completed" },
      { title: "Modernization of High Voltage Engineering Laboratory", pi: "P M Chandrashekharaiah", funder: "AICTE, New Delhi (MODROBS)", amount: "₹7.00 L", status: "completed" },
    ],
    patents: [
      { faculty: "Dr. Basavarajappa S R · Dr. Manjunatha H M · Karibasavaraju T S · Arunkumar P · Ashwini A R", title: "Electric Vehicle Charging Station with Integrated Solar-Tracking Equipment", office: "Intellectual Property Office, UK (Design)", number: "6512906", published: "15/03/2026", granted: "31/03/2026" },
      { faculty: "Dr. Anjaneya L H", title: "Sleek Solar-Powered Streetlight with Built-In Sensors", office: "Intellectual Property Office, UK (Design)", number: "6447008", published: "25/05/2025", granted: "13/06/2025" },
      { faculty: "Dr. Shivakumaraswamy G M", title: "Simulation-Based Design and Control System for an Auxiliary Converter in 6000 HP Electric Locomotives", office: "The Patent Office, Government of India", number: "202541070684", published: "01/08/2025" },
      { faculty: "Dr. Shivakumaraswamy G M", title: "A System and Method for Early Detection of Oral Cancer using Raspberry Pi and Convolutional Neural Networks (CNN)", office: "The Patent Office, Government of India", number: "202541045717", published: "30/05/2025" },
      { faculty: "Dr. Manjunatha H M", title: "Intelligent IoT Cardiac Surveillance System", office: "The Patent Office, Government of India", number: "202441067388A", published: "20/09/2024" },
      { faculty: "Dr. Manjunatha H M", title: "Reliability Assessment of Induction Motor Drive using Failure Mode Effects Analysis", office: "The Patent Office, Government of India", number: "202341059758A", published: "06/10/2023" },
      { faculty: "Dr. Manjunatha H M", title: "Smart Kitchen for Ingredient Measurement through Sensors and Recipe Prediction using Machine Learning Approach", office: "The Patent Office, Government of India", number: "202341045212A", published: "01/09/2023" },
      { faculty: "Dr. Manjunatha H M", title: "Smart Controller for Energy Management of Isolated Solar-Powered Smart Home", office: "The Patent Office, Government of India", number: "202341054549A", published: "01/09/2023" },
      { faculty: "Dr. Manjunatha H M", title: "Optimal Power Allocation for Residential Network in Islanded Microgrid using Capacity Market Demand Response Approach", office: "The Patent Office, Government of India", number: "202341018330A", published: "31/03/2023" },
      { faculty: "Dr. Anjaneya L H", title: "System for Artificial Intelligence-based Transceiving Signal in 6G Wireless Communication Network", office: "The Patent Office, Government of India", number: "202341019163A", published: "31/03/2023" },
      { faculty: "Dr. Manjunatha H M", title: "Solar and Wind Energy Powered Distribution Storage Management System", office: "The Patent Office, Government of India", number: "202341006055A", published: "10/02/2023" },
      { faculty: "Dr. Manjunatha H M", title: "Smart Energy Meter to Incorporate Demand Response", office: "The Patent Office, Government of India", number: "202241076943A", published: "06/01/2023" },
      { faculty: "Dr. Anjaneya L H", title: "An IoT and wireless-based Autonomous Firefighting Drone through Machine Learning", office: "The Patent Office, Government of India", number: "202241055994", published: "07/10/2022" },
      { faculty: "Dr. Anjaneya L H", title: "Radio Frequency Power Amplifier", office: "The Patent Office, Government of India", number: "397075-001", granted: "09/10/2023" },
      { faculty: "Dr. Manjunatha H M", title: "Portable Solar Powered Bathroom Shower", office: "The Patent Office, Government of India", number: "393246-001", granted: "21/08/2023" },
    ],
    partners: [],
    highlights: [],
    contact: {
      coordinator: "Dr. Basavarajappa S R — Associate Professor, EEE",
      email: "basavarajsr@bietdvg.edu",
      room: "Electrical Block · Room EE-101",
    },
  },
  {
    slug: "mathematics",
    name: "Mathematics Research Centre",
    short: "MAT",
    estd: "2008",
    desc: "Biomechanics, fluid mechanics, graph theory and differential geometry.",
    departmentCodes: ["MAT"],
    scholars: 14,
    projects: null,
    pubs: 63,
    intro: [
      "The Department was recognized as a Research Centre under Visvesvaraya Technological University (VTU), Belagavi, in the year 2008. The Department offers Ph.D. programmes in the areas of Biomechanics, Fluid Mechanics, Graph Theory and Differential Geometry.",
      "A total of 9 Ph.D. scholars have been awarded degrees under Mathematics and 14 scholars are currently pursuing their research through the Mathematics Research Centre.",
      "The faculty members of the Department are actively engaged in research and have published numerous high-quality research papers in reputed national and international journals. The Department continues to promote innovative and interdisciplinary research in emerging areas of biomechanics, fluid mechanics, graph theory and differential geometry.",
    ],
    focus: ["Biomechanics", "Fluid Mechanics", "Graph Theory", "Differential Geometry"],
    facultySlugs: [
      "n-s-basavarajappa",
      "ashwini-m-rao",
      "anil-kumar-k-n",
      "vikas-k",
      "santhosh-h-b",
    ],
    ongoing: [],
    patents: [],
    partners: [],
    highlights: [
      "Patent published — Spectrum Sharing in Cognitive Radio Networks with Underlay Constraints, 2023",
    ],
    contact: {
      coordinator: "Dr. Basavarajappa N S — Professor & Head, Mathematics",
      email: "hodmathsbiet@gmail.com",
      room: "Electrical Block · Room EE-309",
    },
  },
  {
    slug: "mca",
    name: "Master of Computer Applications Research Centre",
    short: "MCA",
    estd: "04 October 2024",
    desc: "Innovating smart solutions for a smarter world through artificial intelligence.",
    departmentCodes: ["MCA"],
    scholars: 7,
    projects: null,
    pubs: 2,
    intro: [
      "The MCA research centre was established in 2024 with the vision of innovation, advanced research, and knowledge creation of computer applications. The centre has been encouraging the faculty and students to pursue cutting-edge research and contribute to the academic, industrial, and societal needs.",
    ],
    focus: ["Machine Learning", "Deep Learning", "Artificial Intelligence", "Image Processing"],
    // Dr. Geetha M is named in the return but has no record in staff.ts — see gaps.
    facultySlugs: ["shankaragowda-b-b"],
    ongoing: [],
    patents: [],
    partners: [],
    highlights: [],
    contact: {
      coordinator: "Dr. Shankaragowda B B — Associate Professor & Head, MCA",
      email: "bsgowda2007@gmail.com",
      room: "Room M-301A",
    },
  },
  {
    slug: "mechanical-meric",
    name: "Mechanical Engineering Research and Innovation Centre",
    short: "MERIC",
    estd: "2002",
    desc: "Advancing mechanical design, manufacturing, and innovation through applied research, prototyping, and industry-collaborative projects.",
    departmentCodes: ["ME"],
    deptRaw: "IP, CIVIL, E&E",
    scholars: 1,
    projects: 24,
    pubs: 64,
    // The MERIC return skipped the About, Focus areas and Faculty sections
    // entirely. They are left empty rather than composed from its project list.
    intro: [],
    focus: [],
    facultySlugs: [],
    ongoing: [
      { title: "Innovative Concept for a Zero-Energy Building: Harnessing Passive Solar Architecture and Sustainable Construction Techniques", pi: "Dr. Pradeep N R", funder: "NAIN 2.0 Scheme, KITS — Govt. of Karnataka", amount: "₹2.00 L", status: "completed" },
      { title: "NextGen Eco Aero SmartFarm: Smart Solar-Powered Aeroponic Vertical Farming System Integrating Transparent Cd-Te PV and Ultrasonic Fogger-Based Micro-Irrigation", pi: "Dr. Pradeep N R", funder: "NAIN 2.0 Scheme, KITS — Govt. of Karnataka", amount: "₹4.25 L", status: "ongoing" },
      { title: "Design and Development of a Dye-Synthesized Hybrid Solar Vehicle", pi: "Dr. Pradeep N R", funder: "Visvesvaraya Technological University", amount: "₹12.00 L", status: "ongoing" },
      { title: "Synthesis of Liquid Nano Fertilizer from Sewage Water Using Plasma Technology", pi: "Dr. Sharan A S", funder: "NAIN 2.0 Scheme, KITS — Govt. of Karnataka", amount: "₹3.00 L", status: "completed" },
      { title: "GraphX: Development of Flash Graphene Chamber for Waste Plastic Treatment", pi: "Dr. Sharan A S", funder: "NAIN 2.0 Scheme, KITS — Govt. of Karnataka", amount: "₹3.95 L", status: "ongoing" },
      { title: "AICTE IDEA Lab", pi: "Dr. A G Shankara Murthy", funder: "AICTE, New Delhi", amount: "₹10.00 L", status: "ongoing" },
      { title: "Development of Paper and its by-products using an alternative Agri Waste — Areca nut shell fibres", pi: "Dr. A G Shankara Murthy", funder: "NAIN 2.0 Scheme, K-FIST — Govt. of Karnataka", amount: "₹1.50 L", status: "ongoing" },
      { title: "Development of High Frequency Milling Spindle", pi: "Dr. A G Shankara Murthy", funder: "AICTE RPS", amount: "₹13.60 L", status: "ongoing" },
      { title: "Design and Development of Dye Sensitized Hybrid Solar E-Vehicle", pi: "Dr. A G Shankara Murthy", funder: "VTU, Belagavi", amount: "₹12.00 L", status: "ongoing" },
      { title: "Synergy of Sun and Wind Energy: A Hybrid Power Generation System with Dual-Axis Solar Panels and VAWT", pi: "Dr. Vinayaka Patil A B", funder: "KSCST", amount: "₹0.06 L", status: "completed" },
      { title: "Nano Coating with CuTiO₂ Composite on Mild Steel Substrate by Electroplating Technique for the Improvement of Mechanical Properties", pi: "Dr. Sushma A Patil", funder: "KSCST", amount: "₹0.05 L", status: "completed" },
      { title: "Tomato Grand Challenge — SMART Tomato Preservation System: Acoustic Innovation for Extended Shelf Life", pi: "Dr. G Manavendra", funder: "Govt. of India, Department of Consumer Affairs", amount: "₹0.50 L", status: "completed" },
    ],
    patents: [],
    partners: [
      "NAIN 2.0 Scheme, KITS — Govt. of Karnataka",
      "Visvesvaraya Technological University (VTU Belagavi)",
      "AICTE New Delhi (incl. AICTE RPS)",
      "K-FIST, Govt. of Karnataka",
      "KSCST",
      "Govt. of India, Department of Consumer Affairs",
    ],
    highlights: [
      "Patent granted for “Solar Powered Optical Pyrometer” (Vinayaka Patil A B), 24 March 2025",
      "7 patents published or filed (2022–2026), 6 currently under examination",
      "46 papers published in indexed journals in the last five years, mostly Scopus-indexed",
      "2 book chapters authored by department faculty",
      "4 Ph.D. scholars awarded degrees under department faculty guidance (2022–2024)",
    ],
    contact: {
      coordinator: "Dr. Pradeep N R — Assistant Professor, Mechanical Engineering",
      email: "pradeepnr@bietdvg.edu",
      room: "Dept. of ME · 1st Floor · Room M-104",
    },
  },
  {
    slug: "materials-physics-photonics",
    name: "Centre for Materials Physics and Photonics",
    short: "CMPP",
    estd: "2022",
    desc: "Thin films, nonlinear optics, crystal growth, nanomaterials, renewable energy, density functional theory and energy storage devices.",
    departmentCodes: ["PHY"],
    scholars: 6,
    projects: null,
    pubs: 46,
    intro: [
      "The Centre for Materials Physics and Photonics provides an interdisciplinary platform for research in materials science, condensed matter physics, and photonic technologies. The centre brings together experimental and computational methods to understand material properties and develop solutions for optical, electronic, energy, and nonlinear optical applications. Its major research areas include nonlinear optical materials, nanomaterials, semiconductors, quantum materials, and photonic systems.",
      "The centre uses DFT and quantum chemical calculations alongside experimental studies to understand electronic structure, molecular properties, optical behaviour, and structure–property relationships. It supports collaborative research through shared experimental and computational facilities, student training, and interaction with academic and research institutions. By combining expertise, infrastructure, and innovation, the centre aims to develop advanced materials and photonic technologies for future scientific and technological needs.",
    ],
    focus: [
      "Organic & Inorganic Single Crystals",
      "Nanomaterials",
      "Thin Films",
      "Ultrafast Laser Regime",
      "Third-Order NLO",
      "Thermoelectric Materials",
      "Supercapacitors",
      "Density Functional Theory",
      "Quantum Chemical Calculations",
    ],
    facultySlugs: [
      "jagadeesh-m-r",
      "santhosh-kumar-m-v",
      "t-k-krishnakumar",
      "vinay-parol",
    ],
    ongoing: [],
    patents: [],
    partners: [],
    highlights: [
      "One design patent granted — Solar Powered Optical Pyrometer, March 2025",
    ],
    contact: {
      coordinator: "Dr. Jagadeesh M R — Associate Professor & Head, Physics",
      email: "jagadeeshmr@bietdvg.edu",
      room: "Science & Textile Block · Room T-205",
    },
  },
  {
    slug: "textile-technology",
    name: "Department of Textile Technology and Research Centre",
    short: "TEXRND",
    estd: "2001",
    desc: "Sustainable, technical, smart and medical textiles — materials, processing, nonwovens and functional textile applications.",
    departmentCodes: ["TX", "BT", "CHE", "ME"],
    deptRaw: "TEX, BIO, CHE, ME",
    scholars: 14,
    projects: 4,
    pubs: 24,
    intro: [
      "The Centre for Textile Technology and Innovation is established to promote advanced research, innovation, education, and industry collaboration in the field of textiles and apparel. The Centre focuses on developing sustainable, functional, technical, and smart textile solutions addressing emerging industrial and societal needs. It brings together faculty members, researchers, students, industry professionals, and external collaborators to undertake interdisciplinary research and knowledge-sharing activities. The Centre supports experimental studies, product development, testing, characterization, and technology transfer through shared laboratory facilities and expertise.",
      "The Centre's major thrust areas include sustainable textile processing, technical and medical textiles, smart textiles, nonwoven materials, functional finishes, textile composites, advanced manufacturing, and circular textile technologies. It aims to strengthen industry–academia collaboration, support student innovation, facilitate research publications and patents, and contribute to the development of commercially relevant and environmentally responsible textile technologies.",
    ],
    focus: [
      "Sustainable Textiles",
      "Technical Textiles",
      "Smart Textiles",
      "Medical Textiles",
      "Textile Materials",
      "Nonwoven Textiles",
      "Functional Textile Finishes",
      "Antimicrobial Textiles",
      "Textile Processing",
      "Sustainable Dyeing and Finishing",
      "Textile Composites",
      "Apparel Technology",
      "Garment Manufacturing",
      "Textile Testing and Characterization",
      "Textile Waste Management",
      "Natural and Bio-Based Textiles",
    ],
    // The return left the faculty section as unedited template text — see gaps.
    facultySlugs: [],
    // The return put "Completed" in the Principal Investigator column for all
    // three rows, so no PI is known. Left blank rather than guessed.
    ongoing: [
      { title: "Modification of hand-operated khadi yarn spinning machine for improved yarn quality and production", pi: "", funder: "NAIN 2.0", amount: "₹3.00 L", status: "completed" },
      { title: "Development of bio-composites from areca husk fibres for sound and thermal insulation application", pi: "", funder: "NAIN 2.0", amount: "₹2.00 L", status: "completed" },
      { title: "Development of bio-composites from areca husk fibres for sound and thermal insulation application", pi: "", funder: "Nirman 6.0", amount: "₹0.05 L", status: "completed" },
    ],
    patents: [],
    partners: [],
    highlights: [],
    contact: {
      coordinator: "S M Chandrashekhar — Associate Professor, Textile Technology",
      email: "smcdvg@gmail.com",
      room: "Textile Research Block · Room T-202",
    },
  },
];

/**
 * Institute-wide research overview — section 4 of the return, filled by the
 * Dean (Research) office. Drives the /research hero, the stat grid and the
 * ResearchWeb signature, all of which previously hardcoded their own numbers.
 */
export const INSTITUTE_RESEARCH = {
  /** The /research hero's lead. Deliberately states no figures: scholars,
      grants and publications change week to week, and a sentence quoting
      them goes stale long before anyone edits it. */
  headline:
    "The institute's VTU-recognised research centres host Ph.D. scholars across its departments, are supported by grants from agencies such as VGST, K-FIST, AICTE, NAIN, VTU, GOI and KSCST, and publish indexed research.",
  centres: 15,
  scholars: 117,
  grantsCr: 1.49,
  publications: 348,
  /** Shown as the funder/partner grid at the foot of /research. */
  funders: ["VGST", "DST", "K-FIST", "AICTE", "NAIN", "VTU", "GOI", "KSCST"],
} as const;

export function getCenter(slug: string): ResearchCenter | undefined {
  return CENTERS.find((c) => c.slug === slug);
}

/** Research centres operated by a department. Used by the department detail page. */
export function getCentersByDept(code: string): ResearchCenter[] {
  return CENTERS.filter((c) => c.departmentCodes.includes(code));
}

/**
 * Open items from the return, for the Dean (Research) office. Not rendered —
 * this is the chase-list, kept next to the data so it stays honest as fields
 * get filled in. Delete an entry once the answer lands in CENTERS above.
 */
export const RESEARCH_DATA_GAPS: { centre: string; item: string }[] = [
  { centre: "MERIC", item: "About the Centre, Focus areas and Faculty sections were skipped entirely — the detail page renders without them." },
  { centre: "MERIC", item: "Operating departments answered “IP, CIVIL, E&E” for a Mechanical centre; mapped to ME pending confirmation." },
  { centre: "MERIC", item: "Card says 24 funded projects; the return lists 12, and its own highlight says ₹38.7 L across 12 — those 12 rows sum to ₹62.9 L." },
  { centre: "Textile", item: "Faculty section left as unedited template text — no faculty named." },
  { centre: "Textile", item: "Project table has “Completed” in the Principal Investigator column for all three rows; PIs unknown." },
  { centre: "Chemical Engineering", item: "Room / location left as “(fill in)”." },
  { centre: "Chemical Engineering", item: "Funders were filed under Highlights and the Funders field left as the form's examples; carried as partners pending confirmation." },
  { centre: "Chemical Engineering", item: "G P Desai is named on the faculty list but retired (relieved 31-07-2025) and is no longer in staff.ts — confirm whether he should still be listed." },
  { centre: "MCA", item: "Dr. Geetha M is named on the faculty list but has no record in staff.ts." },
  { centre: "CMPP", item: "Coordinator email was submitted as jagadeeshmr@beitdvg.edu (“beit”); corrected to bietdvg.edu — confirm." },
  { centre: "Several", item: "Personal Gmail addresses submitted instead of institutional ones: CS&E, Chemistry, Mathematics, MCA, Textile." },
  { centre: "ENV & CV", item: "Operating departments answered “PEV, PCV”, which are not department codes; mapped to CV + ENV." },
  { centre: "Institute", item: "Overview claims 15 centres; 11 returned a block. No return from Information Science, Biotechnology or CS&BS, which departments.ts records as recognised centres." },
  { centre: "Institute", item: "Publication counts across the 11 returns sum to 412, not the 348 given institute-wide — the difference is exactly MERIC's 64." },
  { centre: "Institute", item: "₹1.49 cr is labelled “active grants” but equals the sum of all listed projects, completed ones included." },
  { centre: "All", item: "Every block has an asset checklist (banner, funder logos, 2+ lab photos) but no images were ingested; the centre pages render no imagery." },
];
